import { useState, useRef, useEffect } from 'react';
import { useLang } from '../hooks/useLang';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp, Zap, Battery, Building2 } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { t } from '../translations';

// ── Animation presets ─────────────────────────────────────────────────────────
const FU0 = { initial:{opacity:0,y:30}, whileInView:{opacity:1,y:0}, viewport:{once:true}, transition:{duration:0.7} };
const FU1 = { initial:{opacity:0,y:30}, whileInView:{opacity:1,y:0}, viewport:{once:true}, transition:{duration:0.7,delay:0.1} };
const FU2 = { initial:{opacity:0,y:30}, whileInView:{opacity:1,y:0}, viewport:{once:true}, transition:{duration:0.7,delay:0.2} };
const FI  = { initial:{opacity:0},      whileInView:{opacity:1},      viewport:{once:true}, transition:{duration:0.6} };

// ── SEO meta per language ─────────────────────────────────────────────────────
const OG_IMAGE = 'https://koberholding.com/og-transformers.jpg';

const SEO = {
  EN: {
    title:    'Transformers, BESS & HV Substations | KÖBER Energy Holding',
    description: 'HV, MV and LV power transformers (100 kVA – 250 MVA), battery energy storage systems and high-voltage substations from KÖBER Energy Holding. Technical data, product configurator and RFQ for your project.',
    ogTitle:  'Transformers, BESS & HV Substations | KÖBER Energy Holding',
    ogDesc:   'Configure HV/MV/LV power transformers, BESS and substations. Full technical spec sheet, product configurator and RFQ — KÖBER Energy Holding.',
    locale:   'en_US',
  },
  RO: {
    title:    'Transformatoare, BESS și Stații HT | KÖBER Energy Holding',
    description: 'Transformatoare de putere HT, MT și JT (100 kVA – 250 MVA), sisteme de stocare a energiei cu baterii și stații de înaltă tensiune de la KÖBER Energy Holding. Date tehnice, configurator de produse și RFQ pentru proiectul dvs.',
    ogTitle:  'Transformatoare, BESS și Stații HT | KÖBER Energy Holding',
    ogDesc:   'Configurați transformatoare HT/MT/JT, BESS și stații electrice. Fișă tehnică completă, configurator de produse și RFQ — KÖBER Energy Holding.',
    locale:   'ro_RO',
  },
};

// ── Bilingual page data ───────────────────────────────────────────────────────
const DATA = {
  EN: {
    back: "Back",
    hero: {
      eyebrow: "Grid Equipment · KÖBER Energy Holding",
      h1: ["Transformers.", "BESS & PCS.", "HV Substations."],
      factory_label: "New Braila Free Zone Factory",
      factory_date: "Start Q2 2027 delivery",
      desc: "HV, MV and LV transformers, battery energy storage systems and high-voltage substations, with the technical data an engineer needs and one form to define the requirements of a project.",
      tags: ["IEC / EN standards", "Equipment supply", "System integration", "EPC scope"],
      cta: "Configure Equipment",
    },
    stats: [
      { value: "1.1 – 550", unit: "kV",  label: "Highest voltage for equipment, Um" },
      { value: "0.1 – 250", unit: "MVA", label: "Transformer rated power" },
      { value: "5.0",       unit: "MWh", label: "Energy per 20-ft BESS container" },
      { value: "110 – 500", unit: "kV",  label: "Substation voltage levels" },
    ],
    expertise: {
      years: "57",
      strong: "years of expertise in the field.",
      sub: "JSB transformers, BESS and PCS units, now built in the new Braila Free Zone factory.",
    },
    families_eyebrow: "Equipment families",
    families_title: "Three families, one specification.",
    families_desc: "Read the technical data for each family, then configure your project. Everything ends up on a single technical requirements sheet.",
    families: [
      {
        icon: Zap,
        tag: "HV / MV / LV",
        title: "Transformers",
        range: "100 kVA – 250 MVA · IEC 60076 series",
        features: [
          "HV power, MV collector and inverter-duty",
          "Liquid-immersed and cast-resin dry-type",
          "Ecodesign Tier 2 losses",
          "OLTC with vacuum or oil interrupter",
        ],
        types: ["HV power", "MV power & collector", "MV / LV distribution", "LV & special"],
        standards: "IEC 60076 series · IEC 60214-1 · IEC 60296 · EN 50708 · EU 548/2014 and 2019/1783",
        note: "Final ratings, losses, dimensions and guaranteed values are fixed in the approved project specification.",
      },
      {
        icon: Battery,
        tag: "ESS / BESS · IEC 62933 series",
        title: "Energy Storage Systems",
        range: "Utility DC block · AC block with MV skid",
        features: [
          "LFP, liquid-cooled 20-ft container, ≥ 8000 cycles",
          "Up to 1500 V DC, central or string PCS",
          "EMS / SCADA, grid-forming option",
          "IEC 62619 · UL 9540 · NFPA 855",
        ],
        types: ["Utility-scale DC block", "AC block / MV skid", "C&I cabinet"],
        applications: [
          "FCR · aFRR · mFRR", "Energy arbitrage", "Peak shaving",
          "PV / wind co-location", "Capacity mechanism", "Black start",
          "Islanding / microgrid", "Reactive power support", "Synthetic inertia",
        ],
        standards: "IEC 62933 series · IEC 62619 · IEC 63056 · UN 38.3 · UL 9540 · UL 9540A · UL 1973 · NFPA 855 · EU Battery Regulation 2023/1542",
      },
      {
        icon: Building2,
        tag: "110 – 500 kV · IEC 61936-1 · EN 50522",
        title: "HV Substations",
        range: "110 / 220 / 400 / 500 kV · 1250 – 4000 A",
        features: [
          "AIS, GIS, hybrid, compact, mobile",
          "25 – 63 kA short-circuit rating",
          "IEC 61850 protection and control",
          "EU F-gas 2024/573 compliant",
        ],
        types: ["AIS (air-insulated)", "GIS (gas-insulated)", "Hybrid / compact / mobile"],
        standards: "IEC 61936-1 · EN 50522 · IEEE 80 · IEC 62271-100/-102/-200/-203 · IEC 61850 · EU 2024/573",
        scope: "Primary equipment, protection and control, civil works and system studies in one scope.",
      },
    ],
    integration: {
      eyebrow: "System integration",
      title: "From the grid to the battery.",
      desc: "A single-line view of a grid-connected storage plant. Each component links to the parameters that define it in the configurator.",
      nodes: [
        { name: "Grid connection",         detail: "110 / 220 / 400 / 500 kV · Switchgear · CT/VT · arrester" },
        { name: "HV bay",                  detail: "AIS · GIS · hybrid · IEC 62271-100 · 25 – 63 kA" },
        { name: "Power transformer",       detail: "HV/MV · YNd11 · OLTC · 16 – 250 MVA · IEC 60076" },
        { name: "MV switchgear",           detail: "20 / 33 kV · LSC2B · IAC AFLR · IEC 62271-200" },
        { name: "Skid transformer",        detail: "MV / 0.69 kV · inverter duty · 2.5 – 6.3 MVA per skid" },
        { name: "Power conversion system", detail: "Grid-following or grid-forming · 2.5 – 5 MVA per skid" },
        { name: "Battery containers",      detail: "LFP · 20-ft · 5.0 MWh · ≥ 8000 cycles · up to 1500 V DC" },
      ],
    },
    delivery: {
      eyebrow: "Project delivery",
      title: "From specification to energisation.",
      steps: [
        { num: "01", title: "Specification",                  desc: "Your project data and technical requirements from the configurator." },
        { num: "02", title: "Engineering review",             desc: "Sizing, interfaces and the open items left for KÖBER to propose." },
        { num: "03", title: "Technical & commercial offer",   desc: "Defined scope, guaranteed values, schedule and terms." },
        { num: "04", title: "Manufacturing & FAT",            desc: "Approved design, production and factory acceptance testing." },
        { num: "05", title: "Delivery",                       desc: "Transport and logistics to the agreed Incoterm." },
        { num: "06", title: "Installation, SAT & commissioning", desc: "Site acceptance tests and energisation." },
      ],
    },
    standards: {
      eyebrow: "Standards & compliance",
      title: "Referenced standards.",
      desc: "Applicable editions, test evidence and national grid-code requirements are confirmed per project. This list is not a claim of certification.",
      groups: [
        {
          title: "Transformers",
          items: ["IEC 60076 series", "IEC 60214-1 (tap changers)", "IEC 60296", "IEC 62770", "IEC 61099", "EN 50708", "ISO 12944", "EU 548/2014 and 2019/1783"],
        },
        {
          title: "Energy Storage Systems",
          items: ["IEC 62933 series", "IEC 62619", "IEC 63056", "UN 38.3", "UL 9540 · UL 9540A", "UL 1973", "NFPA 855", "EU Battery Regulation 2023/1542", "EU RfG 2016/631", "EN 50549-2"],
        },
        {
          title: "HV Substations",
          items: ["IEC 61936-1 · EN 50522 · IEEE 80", "IEC 62271-100 / -102 / -200 / -203", "IEC 61850 · IEC 61000-4-30", "IEC 60071 · IEC 62305 · EU 2024/573"],
        },
      ],
    },
    configurator: {
      eyebrow: "Product configurator",
      title: "Define your technical requirements.",
      desc: "Five steps, one form for all three families. Only your contact details are mandatory; leave any technical field open and KÖBER engineering will propose it.",
    },
    contact: {
      eyebrow: "Your next project",
      title: "Start with the specification.",
      offices: [
        { title: "Bucharest Office", address: "18 Cluceru Udricani Street, 030782,\n3rd District, Bucharest, Romania", email: "sales@koberholding.com" },
        { title: "Brăila Free Zone Factory", address: "Soseaua de Centura,\nCalea Călărașilor 21A,\n810336 Brăila, Romania", email: "office@koberholding.com" },
      ],
    },
  },
  RO: {
    back: "Înapoi",
    hero: {
      eyebrow: "Echipamente de rețea · KÖBER Energy Holding",
      h1: ["Transformatoare.", "BESS & PCS.", "Stații HT."],
      factory_label: "Noua fabrică din Zona Liberă Brăila",
      factory_date: "Livrare din T2 2027",
      desc: "Transformatoare HT, MT și JT, sisteme de stocare a energiei și stații de înaltă tensiune, cu datele tehnice de care are nevoie un inginer și un singur formular pentru cerința de proiect.",
      tags: ["Standarde IEC / EN", "Furnizare echipamente", "Integrare sistem", "Domeniu EPC"],
      cta: "Configurați Echipamentul",
    },
    stats: [
      { value: "1,1 – 550", unit: "kV",  label: "Cea mai înaltă tensiune pentru echipament, Um" },
      { value: "0,1 – 250", unit: "MVA", label: "Puterea nominală a transformatorului" },
      { value: "5,0",       unit: "MWh", label: "Energie per container BESS de 20 ft" },
      { value: "110 – 500", unit: "kV",  label: "Niveluri de tensiune pentru stații" },
    ],
    expertise: {
      years: "57",
      strong: "de ani de expertiză în domeniu.",
      sub: "Transformatoare, unități BESS și PCS JSB, produse acum în noua fabrică din Zona Liberă Brăila.",
    },
    families_eyebrow: "Familii de echipamente",
    families_title: "Trei familii, o singură specificație.",
    families_desc: "Consultați datele tehnice pentru fiecare familie, apoi configurați proiectul. Totul ajunge pe o singură fișă de cerințe tehnice.",
    families: [
      {
        icon: Zap,
        tag: "HT / MT / JT",
        title: "Transformatoare",
        range: "100 kVA – 250 MVA · Seria IEC 60076",
        features: [
          "Transformatoare de putere HT, colectoare MT și pentru invertoare",
          "Imersate în lichid și uscate cu rășină",
          "Pierderi Ecodesign Tier 2",
          "RAST cu întrerupător în vid sau în ulei",
        ],
        types: ["Putere HT", "Putere MT & colector", "Distribuție MT/JT", "JT & speciale"],
        standards: "Seria IEC 60076 · IEC 60214-1 · IEC 60296 · EN 50708 · UE 548/2014 și 2019/1783",
        note: "Valorile finale ale puterii, pierderilor, dimensiunilor și valorilor garantate sunt stabilite în specificația de proiect aprobată.",
      },
      {
        icon: Battery,
        tag: "ESS / BESS · Seria IEC 62933",
        title: "Sisteme de Stocare a Energiei",
        range: "Bloc DC de uz public · Bloc CA cu skid MT",
        features: [
          "LFP, răcit cu lichid, container 20 ft, ≥ 8000 cicluri",
          "Până la 1500 V CC, PCS central sau string",
          "EMS / SCADA, opțiune grid-forming",
          "IEC 62619 · UL 9540 · NFPA 855",
        ],
        types: ["Bloc DC la scară utilitară", "Bloc CA / skid MT", "Cabinet C&I"],
        applications: [
          "FCR · aFRR · mFRR", "Arbitraj energetic", "Tăiere vârf de consum",
          "Co-localizare PV / eolian", "Mecanism de capacitate", "Black start",
          "Insulare / microgrid", "Suport putere reactivă", "Inerție sintetică",
        ],
        standards: "Seria IEC 62933 · IEC 62619 · IEC 63056 · UN 38.3 · UL 9540 · UL 9540A · UL 1973 · NFPA 855 · Regulamentul UE privind bateriile 2023/1542",
      },
      {
        icon: Building2,
        tag: "110 – 500 kV · IEC 61936-1 · EN 50522",
        title: "Stații HT",
        range: "110 / 220 / 400 / 500 kV · 1250 – 4000 A",
        features: [
          "AIS, GIS, hibrid, compact, mobil",
          "Putere de scurtcircuit 25 – 63 kA",
          "Protecție și control IEC 61850",
          "Conform UE F-gas 2024/573",
        ],
        types: ["AIS (izolate în aer)", "GIS (izolate în gaz)", "Hibrid / compact / mobil"],
        standards: "IEC 61936-1 · EN 50522 · IEEE 80 · IEC 62271-100/-102/-200/-203 · IEC 61850 · UE 2024/573",
        scope: "Echipamente primare, protecție și control, lucrări civile și studii de sistem într-un singur domeniu.",
      },
    ],
    integration: {
      eyebrow: "Integrare sistem",
      title: "De la rețea la baterie.",
      desc: "O schemă unifilară a unei centrale de stocare conectate la rețea. Fiecare componentă se leagă de parametrii care o definesc în configurator.",
      nodes: [
        { name: "Racord la rețea",           detail: "110 / 220 / 400 / 500 kV · Echipament de comutație · TC/TT · descărcător" },
        { name: "Celulă HT",                 detail: "AIS · GIS · hibrid · IEC 62271-100 · 25 – 63 kA" },
        { name: "Transformator de putere",   detail: "HT/MT · YNd11 · RAST · 16 – 250 MVA · IEC 60076" },
        { name: "Echipament de comutație MT",detail: "20 / 33 kV · LSC2B · IAC AFLR · IEC 62271-200" },
        { name: "Transformator de skid",     detail: "MT / 0,69 kV · pentru invertoare · 2,5 – 6,3 MVA per skid" },
        { name: "Sistem de conversie a energiei", detail: "Grid-following sau grid-forming · 2,5 – 5 MVA per skid" },
        { name: "Containere baterii",        detail: "LFP · 20 ft · 5,0 MWh · ≥ 8000 cicluri · până la 1500 V CC" },
      ],
    },
    delivery: {
      eyebrow: "Livrare proiect",
      title: "De la specificație la punere în funcțiune.",
      steps: [
        { num: "01", title: "Specificație",                       desc: "Datele proiectului și cerințele tehnice din configurator." },
        { num: "02", title: "Revizie inginerie",                  desc: "Dimensionare, interfețe și elementele deschise pe care KÖBER le propune." },
        { num: "03", title: "Ofertă tehnică & comercială",        desc: "Domeniu definit, valori garantate, program și condiții." },
        { num: "04", title: "Fabricație & FAT",                   desc: "Proiectare aprobată, producție și teste de acceptanță la fabrică." },
        { num: "05", title: "Livrare",                            desc: "Transport și logistică la Incoterm-ul agreat." },
        { num: "06", title: "Instalare, SAT & punere în funcțiune", desc: "Teste de acceptanță pe amplasament și punere sub tensiune." },
      ],
    },
    standards: {
      eyebrow: "Standarde & conformitate",
      title: "Standarde de referință.",
      desc: "Edițiile aplicabile, dovezile de testare și cerințele naționale de cod de rețea sunt confirmate per proiect. Această listă nu reprezintă o declarație de certificare.",
      groups: [
        {
          title: "Transformatoare",
          items: ["Seria IEC 60076", "IEC 60214-1 (regulatoare de tensiune)", "IEC 60296", "IEC 62770", "IEC 61099", "EN 50708", "ISO 12944", "UE 548/2014 și 2019/1783"],
        },
        {
          title: "Sisteme de Stocare a Energiei",
          items: ["Seria IEC 62933", "IEC 62619", "IEC 63056", "UN 38.3", "UL 9540 · UL 9540A", "UL 1973", "NFPA 855", "Regulamentul UE privind bateriile 2023/1542", "EU RfG 2016/631", "EN 50549-2"],
        },
        {
          title: "Stații HT",
          items: ["IEC 61936-1 · EN 50522 · IEEE 80", "IEC 62271-100 / -102 / -200 / -203", "IEC 61850 · IEC 61000-4-30", "IEC 60071 · IEC 62305 · UE 2024/573"],
        },
      ],
    },
    configurator: {
      eyebrow: "Configurator de produse",
      title: "Definiți cerințele dvs. tehnice.",
      desc: "Cinci pași, un formular pentru toate trei familiile. Numai datele dvs. de contact sunt obligatorii; lăsați orice câmp tehnic deschis și ingineria KÖBER îl va propune.",
    },
    contact: {
      eyebrow: "Proiectul dvs. următor",
      title: "Începeți cu specificația.",
      offices: [
        { title: "Birou București", address: "Str. Cluceru Udricani 18, 030782,\nSectorul 3, București, România", email: "sales@koberholding.com" },
        { title: "Fabrică Zona Liberă Brăila", address: "Șoseaua de Centură,\nCalea Călărașilor 21A,\n810336 Brăila, România", email: "office@koberholding.com" },
      ],
    },
  },
};

// ── Sub-components ────────────────────────────────────────────────────────────

function FamilyCard({ fam, index, lang }) {
  const [open, setOpen] = useState(false);
  const Icon = fam.icon;
  return (
    <motion.div
      {...FU0}
      transition={{ duration: 0.7, delay: index * 0.12 }}
      className="border border-[#D4A017]/15 bg-[#070707] hover:border-[#D4A017]/30 transition-colors duration-300"
      data-testid={`family-card-${index}`}
    >
      {/* Header */}
      <div className="p-8 border-b border-[#D4A017]/10">
        <span className="text-xs tracking-widest uppercase text-[#D4A017]/60">{fam.tag}</span>
        <div className="flex items-start justify-between mt-4 gap-4">
          <h3 className="text-2xl font-semibold text-white" style={{ fontFamily: "'Outfit',sans-serif" }}>{fam.title}</h3>
          <Icon size={22} className="text-[#D4A017]/50 flex-shrink-0 mt-1" />
        </div>
        <p className="text-xs text-white/35 mt-2 font-mono">{fam.range}</p>
      </div>

      {/* Features */}
      <div className="p-8">
        <ul className="space-y-3 mb-6">
          {fam.features.map(f => (
            <li key={f} className="flex items-start gap-3 text-sm text-white/65">
              <span className="w-1 h-1 rounded-full bg-[#D4A017] flex-shrink-0 mt-2" />
              {f}
            </li>
          ))}
        </ul>

        {/* Type tabs (chips) */}
        <div className="flex flex-wrap gap-2 mb-6">
          {fam.types.map(ty => (
            <span key={ty} className="text-xs px-3 py-1 border border-white/10 text-white/40">{ty}</span>
          ))}
        </div>

        {/* Applications (BESS only) */}
        {fam.applications && (
          <div className="mb-6">
            <p className="text-xs tracking-widest uppercase text-white/25 mb-3">
              {lang === 'RO' ? 'Aplicații' : 'Applications'}
            </p>
            <div className="flex flex-wrap gap-2">
              {fam.applications.map(a => (
                <span key={a} className="text-xs px-2 py-1 bg-[#D4A017]/6 border border-[#D4A017]/18 text-[#D4A017]/60">{a}</span>
              ))}
            </div>
          </div>
        )}

        {/* Scope note (Substations) */}
        {fam.scope && (
          <p className="text-xs text-white/35 italic border-l-2 border-[#D4A017]/25 pl-3 mb-5">{fam.scope}</p>
        )}

        {/* Expandable standards */}
        <button
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-2 text-xs text-white/35 hover:text-[#D4A017]/70 transition-colors mt-2"
        >
          {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          <span className="tracking-widest uppercase">
            {lang === 'RO' ? 'Standarde' : 'Standards'}
          </span>
        </button>
        {open && (
          <p className="text-xs text-white/30 mt-3 leading-relaxed font-mono border-t border-white/5 pt-3">{fam.standards}</p>
        )}
        {open && fam.note && (
          <p className="text-xs text-white/25 italic mt-2 border-l border-[#D4A017]/20 pl-3">{fam.note}</p>
        )}
      </div>
    </motion.div>
  );
}

function DeliveryStep({ step, index }) {
  return (
    <motion.div
      {...FU0}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      className="relative p-6 border border-[#D4A017]/12 bg-[#0D0D0D] hover:border-[#D4A017]/28 transition-colors duration-300 group"
    >
      <div className="text-3xl font-bold text-[#D4A017]/12 mb-3 group-hover:text-[#D4A017]/22 transition-colors"
        style={{ fontFamily: "'Outfit',sans-serif" }}>{step.num}</div>
      <h4 className="text-sm font-semibold text-white mb-2">{step.title}</h4>
      <p className="text-xs text-white/40 leading-relaxed">{step.desc}</p>
    </motion.div>
  );
}

// Hero animation constants — module-scope to avoid new object refs on every render
const GP_ANIM_BACK  = { initial:{opacity:0,x:-16}, animate:{opacity:1,x:0}, transition:{duration:0.5} };
const GP_ANIM_EYE   = { initial:{opacity:0,y:20},  animate:{opacity:1,y:0}, transition:{duration:0.6,delay:0.1} };
const GP_ANIM_H1    = { initial:{opacity:0,y:30},  animate:{opacity:1,y:0}, transition:{duration:0.8,delay:0.25} };
const GP_ANIM_DESC1 = { initial:{opacity:0,y:24},  animate:{opacity:1,y:0}, transition:{duration:0.7,delay:0.45} };
const GP_ANIM_DESC2 = { initial:{opacity:0,y:24},  animate:{opacity:1,y:0}, transition:{duration:0.7,delay:0.5} };
const GP_ANIM_TAGS  = { initial:{opacity:0,y:16},  animate:{opacity:1,y:0}, transition:{duration:0.6,delay:0.65} };
const GP_ANIM_CTA   = { initial:{opacity:0,y:16},  animate:{opacity:1,y:0}, transition:{duration:0.6,delay:0.75} };
const GP_ANIM_STATS = { initial:{opacity:0,x:30},  animate:{opacity:1,x:0}, transition:{duration:0.8,delay:0.4} };
const GRID_STYLE    = {
  backgroundImage: `linear-gradient(rgba(212,160,23,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(212,160,23,0.035) 1px, transparent 1px)`,
  backgroundSize: '80px 80px',
};

// ── Page sub-components ────────────────────────────────────────────────────────

function GPHero({ d, navigate, onConfigure }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      <div className="absolute inset-0 pointer-events-none" style={GRID_STYLE} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-[#050505]" />
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 w-full">
        <motion.button {...GP_ANIM_BACK} onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-xs tracking-widest uppercase text-white/35 hover:text-white/70 transition-colors mb-16">
          <ArrowLeft size={13} /> {d.back}
        </motion.button>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <motion.p {...GP_ANIM_EYE} className="text-xs tracking-widest uppercase text-[#D4A017]/70 mb-5">{d.hero.eyebrow}</motion.p>
            <motion.h1 {...GP_ANIM_H1} className="text-5xl sm:text-6xl lg:text-7xl font-semibold leading-tight mb-6" style={{ fontFamily:"'Outfit',sans-serif" }}>
              {d.hero.h1.map((line) => <span key={line} className="block">{line}</span>)}
            </motion.h1>
            <motion.p {...GP_ANIM_DESC1} className="text-sm text-white/50 mb-2">
              <span className="text-[#D4A017]/80 font-medium">{d.hero.factory_label}</span>
              {' · '}
              <span className="text-white/70 font-semibold">{d.hero.factory_date}</span>
            </motion.p>
            <motion.p {...GP_ANIM_DESC2} className="text-base text-white/50 leading-relaxed mb-8 max-w-xl">{d.hero.desc}</motion.p>
            <motion.div {...GP_ANIM_TAGS} className="flex flex-wrap gap-3 mb-8">
              {d.hero.tags.map(tag => <span key={tag} className="text-xs px-3 py-1.5 border border-white/12 text-white/45">{tag}</span>)}
            </motion.div>
            <motion.button {...GP_ANIM_CTA} onClick={onConfigure} data-testid="hero-configure-btn"
              className="inline-flex items-center gap-2 text-sm px-7 py-3.5 bg-[#D4A017] text-black font-semibold hover:bg-[#F5B814] active:scale-[0.97] transition-all duration-200"
              style={{ fontFamily:"'Outfit',sans-serif" }}>
              {d.hero.cta}
              <svg viewBox="0 0 14 10" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-3">
                <path d="M0 5h12M8 1l4 4-4 4" />
              </svg>
            </motion.button>
          </div>
          <motion.div {...GP_ANIM_STATS} className="grid grid-cols-2 gap-4">
            {d.stats.map((s, i) => (
              <div key={s.label} className="border border-[#D4A017]/20 bg-black/40 backdrop-blur-sm p-6" data-testid={`stat-card-${i}`}>
                <div className="text-2xl lg:text-3xl font-bold text-[#D4A017]" style={{ fontFamily:"'Outfit',sans-serif" }}>
                  {s.value} <span className="text-lg text-[#D4A017]/60">{s.unit}</span>
                </div>
                <p className="text-xs text-white/40 mt-2 leading-snug">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function GPExpertise({ d }) {
  return (
    <div className="border-y border-[#D4A017]/12 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 py-10 flex flex-wrap items-center gap-6">
        <span className="text-5xl font-bold text-[#D4A017]" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.expertise.years}</span>
        <div>
          <p className="text-lg text-white font-semibold">{d.expertise.strong}</p>
          <p className="text-sm text-white/45 mt-1 max-w-xl">{d.expertise.sub}</p>
        </div>
      </div>
    </div>
  );
}

function GPFamilies({ d, lang }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24">
      <div className="flex flex-col items-start mb-14">
        <motion.p {...FI} className="text-xs tracking-widest uppercase text-[#D4A017]/60 mb-3">{d.families_eyebrow}</motion.p>
        <motion.h2 {...FU0} className="text-4xl sm:text-5xl font-semibold" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.families_title}</motion.h2>
        <motion.p {...FU1} className="text-white/45 text-base mt-4 max-w-2xl">{d.families_desc}</motion.p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {d.families.map((fam, i) => <FamilyCard key={fam.title} fam={fam} index={i} lang={lang} />)}
      </div>
    </section>
  );
}

function GPIntegration({ d }) {
  return (
    <section className="border-y border-[#D4A017]/10 bg-[#080808] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...FI} className="text-xs tracking-widest uppercase text-[#D4A017]/60 mb-3">{d.integration.eyebrow}</motion.p>
        <motion.h2 {...FU0} className="text-4xl sm:text-5xl font-semibold mb-4" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.integration.title}</motion.h2>
        <motion.p {...FU1} className="text-white/45 text-base mb-14 max-w-2xl">{d.integration.desc}</motion.p>
        <div className="relative">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-[#D4A017]/15 hidden sm:block" />
          <div className="space-y-0">
            {d.integration.nodes.map((node, i) => (
              <motion.div key={node.name} {...FU0} transition={{ duration:0.5, delay:i*0.07 }}
                className="flex items-start gap-8 py-5 border-b border-white/5 last:border-0 group">
                <div className="relative flex-shrink-0 w-12 hidden sm:flex items-center justify-center">
                  <div className="w-3 h-3 border border-[#D4A017]/50 bg-[#050505] group-hover:bg-[#D4A017]/20 transition-colors" />
                </div>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <span className="text-sm font-medium text-white/80">{node.name}</span>
                  <span className="text-xs text-white/30 font-mono sm:text-right">{node.detail}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function GPDelivery({ d }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-24">
      <motion.p {...FI} className="text-xs tracking-widest uppercase text-[#D4A017]/60 mb-3">{d.delivery.eyebrow}</motion.p>
      <motion.h2 {...FU0} className="text-4xl sm:text-5xl font-semibold mb-14" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.delivery.title}</motion.h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {d.delivery.steps.map((step, i) => <DeliveryStep key={step.num} step={step} index={i} />)}
      </div>
    </section>
  );
}

function GPStandards({ d }) {
  return (
    <section className="border-t border-[#D4A017]/10 bg-[#080808] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <motion.p {...FI} className="text-xs tracking-widest uppercase text-[#D4A017]/60 mb-3">{d.standards.eyebrow}</motion.p>
        <motion.h2 {...FU0} className="text-4xl sm:text-5xl font-semibold mb-4" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.standards.title}</motion.h2>
        <motion.p {...FU1} className="text-white/40 text-sm mb-12 max-w-2xl">{d.standards.desc}</motion.p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {d.standards.groups.map((grp, i) => (
            <motion.div key={grp.title} {...FU0} transition={{ duration:0.6, delay:i*0.1 }}>
              <h4 className="text-xs tracking-widest uppercase text-[#D4A017]/70 mb-5 border-b border-[#D4A017]/15 pb-3">{grp.title}</h4>
              <ul className="space-y-2.5">
                {grp.items.map(item => (
                  <li key={item} className="text-xs text-white/40 font-mono flex items-start gap-2">
                    <span className="text-[#D4A017]/30 mt-0.5">—</span>{item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GPConfigurator({ d, lang, configuratorRef }) {
  return (
    <section id="configurator" ref={configuratorRef} className="border-t border-[#D4A017]/15 py-20 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <motion.p {...FI} className="text-xs tracking-widest uppercase text-[#D4A017]/60 mb-3">{d.configurator.eyebrow}</motion.p>
        <motion.h2 {...FU0} className="text-4xl sm:text-5xl font-semibold mb-4" style={{ fontFamily:"'Outfit',sans-serif" }}>{d.configurator.title}</motion.h2>
        <motion.p {...FU1} className="text-white/45 text-base max-w-2xl">{d.configurator.desc}</motion.p>
      </div>
      <motion.div {...FI} className="w-full" style={{ height:'100vh', minHeight:'700px' }}>
        <iframe
          src={`/jsb-equipment${lang === 'RO' ? '-ro' : ''}.html#configurator`}
          title="KÖBER Energy Holding Product Configurator"
          data-testid="jsb-configurator-iframe"
          className="w-full h-full border-0"
          style={{ height:'100vh', minHeight:'700px', background:'#050505' }}
          allow="clipboard-write"
        />
      </motion.div>
    </section>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function GPTransformersPage() {
  const navigate      = useNavigate();
  const [lang, setLang] = useLang();
  const [showContact, setShowContact] = useState(false);
  const text          = t[lang];
  const d             = DATA[lang];
  const configuratorRef = useRef(null);

  const scrollToConfigurator = () => {
    configuratorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── SEO: update <title>, <meta description> and OG tags on lang change ────────
  useEffect(() => {
    const get = sel => document.querySelector(sel);
    const setContent = (sel, val) => { const el = get(sel); if (el) el.setAttribute('content', val); };

    const prev = {
      title:   document.title,
      desc:    get('meta[name="description"]')?.content,
      ogTitle: get('meta[property="og:title"]')?.content,
      ogDesc:  get('meta[property="og:description"]')?.content,
      ogUrl:   get('meta[property="og:url"]')?.content,
      ogLoc:   get('meta[property="og:locale"]')?.content,
      twTitle: get('meta[name="twitter:title"]')?.content,
      twDesc:  get('meta[name="twitter:description"]')?.content,
    };

    const s = SEO[lang] ?? SEO.EN;
    const pageUrl = `${window.location.origin}/gp-transformers`;

    document.title = s.title;
    setContent('meta[name="description"]',          s.description);
    setContent('meta[property="og:title"]',         s.ogTitle);
    setContent('meta[property="og:description"]',   s.ogDesc);
    setContent('meta[property="og:url"]',            pageUrl);
    setContent('meta[property="og:locale"]',         s.locale);
    setContent('meta[property="og:image"]',          OG_IMAGE);
    setContent('meta[name="twitter:title"]',         s.ogTitle);
    setContent('meta[name="twitter:description"]',   s.ogDesc);
    setContent('meta[name="twitter:image"]',         OG_IMAGE);

    return () => {
      document.title = prev.title;
      setContent('meta[name="description"]',        prev.desc  ?? '');
      setContent('meta[property="og:title"]',       prev.ogTitle ?? '');
      setContent('meta[property="og:description"]', prev.ogDesc  ?? '');
      setContent('meta[property="og:url"]',          prev.ogUrl   ?? '');
      setContent('meta[property="og:locale"]',       prev.ogLoc   ?? '');
      setContent('meta[name="twitter:title"]',       prev.twTitle ?? '');
      setContent('meta[name="twitter:description"]', prev.twDesc  ?? '');
    };
    // SEO, OG_IMAGE are module-level constants (never change); get/setContent defined inside effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="bg-[#050505] text-white min-h-screen" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <Navbar text={text.nav} lang={lang} setLang={setLang} onContact={() => setShowContact(true)} />
      <GPHero d={d} navigate={navigate} onConfigure={scrollToConfigurator} />
      <GPExpertise d={d} />
      <GPFamilies d={d} lang={lang} />
      <GPIntegration d={d} />
      <GPDelivery d={d} />
      <GPStandards d={d} />
      <GPConfigurator d={d} lang={lang} configuratorRef={configuratorRef} />
      <Footer text={text.footer} lang={lang} setLang={setLang} onContact={() => setShowContact(true)} />
      <ContactModal open={showContact} onClose={() => setShowContact(false)} text={text.contact} lang={lang} />
    </div>
  );
}
